// src/components/tableros/tabs/ColoresTab.js - Reorganizado y corregido
import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  Divider,
  Button,
  Tooltip
} from '@mui/material';
import { ColorLens, Refresh } from '@mui/icons-material';

const ColoresTab = ({
  // Propiedades para elementos básicos de la tabla
  tableBackgroundColor = '#1a1a1a',
  setTableBackgroundColor,
  globalFontColor = '#ffffff',
  setGlobalFontColor,
  tableRowOddColor = '#252525',
  setTableRowOddColor,
  tableRowEvenColor = '#1e1e1e',
  setTableRowEvenColor,

  // Propiedades para cronómetros
  timerColor = '#00ff00',
  setTimerColor,
  largeTimerColor = '#00ff00',
  setLargeTimerColor,

  // Propiedades para encabezados
  headerBackgroundColor = '#2c0369',
  setHeaderBackgroundColor,
  headerFontColor = '#ffffff',
  setHeaderFontColor,
  columnsHeaderColor = '#4c516d',
  setColumnsHeaderColor,

  // Propiedades para elementos específicos
  positionColor = '#ffffff',
  setPositionColor,
  positionBackgroundColor = '#4c516d',
  setPositionBackgroundColor,
  laneColor = '#ffffff',
  setLaneColor,
  numberColor = '#ffffff',
  setNumberColor,
  athleteNameColor = '#ffffff',
  setAthleteNameColor,
  teamNameColor = '#e0e0e0',
  setTeamNameColor,
  nameBackgroundColor = '#515e7b',
  setNameBackgroundColor,
  resultTimeColor = '#ffffff',
  setResultTimeColor,
  markBackgroundColor = '#000000',
  setMarkBackgroundColor,
  attemptColor = '#ffffff',
  setAttemptColor,
  attemptBackgroundColor = '#0d47a1',
  setAttemptBackgroundColor,
  deltaTimeColor = '#c1ff30',
  setDeltaTimeColor,
  lastLapTimeColor = '#ffffff',
  setLastLapTimeColor,
  reactionTimeColor = '#ffffff',
  setReactionTimeColor,
  lapsToGoColor = '#ffffff',
  setLapsToGoColor,
  lapsColumnsColor = '#ffffff',
  setLapsColumnsColor,
  
  // Propiedades de campo vertical
  successColor = '#1b5e20',
  setSuccessColor,
  failColor = '#b71c1c',
  setFailColor,
  passColor = '#0d47a1',
  setPassColor,
  
  // Estado de las columnas seleccionadas (mismas props que ColumnasTab)
  showLane = false,
  showNumber = false,
  showAttemptNumber = false,
  showLastLapTime = false,
  showLapsToGo = false,
  showLapsColumns = false,
  showReactionTime = false,
  showTimeDifference = false,
  
  // Tipo de tablero (para mostrar opciones específicas)
  boardType = 'track', // 'track', 'horizontalField', o 'verticalField'
  displayRows = 1,
  
  // Función para restablecer colores predeterminados
  resetToDefaultColors
}) => {
  const isTrackBoard = boardType === 'track';
  const isFieldBoard = boardType === 'horizontalField' || boardType === 'verticalField';
  const isHorizontalField = boardType === 'horizontalField';
  const isVerticalField = boardType === 'verticalField';
  const isSingleAthleteView = displayRows === 1;
  const isMultiAthleteView = displayRows > 1;
  
  // ColorField es un componente reutilizable para mostrar un campo de color con vista previa
  const ColorField = ({ 
    title, 
    value, 
    onChange, 
    backgroundColor = tableBackgroundColor,
    previewText = null,
    textColor = value,
    fontSize = 14,
    fontFamily = 'inherit',
    height = 60
  }) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 160 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Box 
        sx={{ 
          width: '100%', 
          height: height, 
          backgroundColor: backgroundColor,
          borderRadius: 1,
          mb: 2,
          border: '1px solid rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
      >
        {previewText && (
          <Typography 
            variant="body1" 
            sx={{ 
              color: textColor, 
              fontSize: `${fontSize}px`,
              fontWeight: 'medium',
              fontFamily: fontFamily
            }}
          >
            {previewText}
          </Typography>
        )}
      </Box>
      
      {/* Reemplazar TextField por un control de color personalizado */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Box 
          sx={{ 
            width: 36, 
            height: 36, 
            borderRadius: '4px',
            border: '1px solid rgba(0,0,0,0.23)',
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: '150%',
              height: '150%',
              position: 'absolute',
              top: '-25%',
              left: '-25%',
              cursor: 'pointer',
              opacity: 0 // Hacemos el input invisible
            }}
          />
          <Box 
            sx={{ 
              width: '100%', 
              height: '100%', 
              backgroundColor: value,
              pointerEvents: 'none' // Evita que la caja bloquee los clics
            }} 
          />
        </Box>
        
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <Box 
                sx={{ 
                  width: 16, 
                  height: 16, 
                  backgroundColor: value,
                  borderRadius: '2px',
                  border: '1px solid rgba(0,0,0,0.23)',
                  mr: 1
                }} 
              />
            ),
          }}
        />
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Botón para restablecer colores predeterminados */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Restablecer colores predeterminados">
          <Button 
            variant="outlined" 
            startIcon={<Refresh />} 
            onClick={resetToDefaultColors}
            color="primary"
          >
            Restablecer Colores
          </Button>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* SECCIÓN 1: COLORES BÁSICOS - Para todos los tableros, pero con ajustes según displayRows */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Elementos Básicos de la Tabla
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        
        {/* Fondo de la tabla - Siempre visible */}
        <Grid item xs={12} sm={6} md={3}>
          <ColorField 
            title="Fondo de la Tabla" 
            value={tableBackgroundColor} 
            onChange={setTableBackgroundColor} 
            backgroundColor={tableBackgroundColor}
            height={100}
          />
        </Grid>
        
        {/* Texto global - Siempre visible */}
        <Grid item xs={12} sm={6} md={3}>
          <ColorField 
            title="Texto Global" 
            value={globalFontColor} 
            onChange={setGlobalFontColor} 
            backgroundColor={tableBackgroundColor}
            previewText="Texto de ejemplo"
            textColor={globalFontColor}
            fontSize={18}
            height={100}
          />
        </Grid>
        
        {/* Filas impares y pares - Solo visibles en vista de múltiples atletas */}
        {isMultiAthleteView && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <ColorField 
                title="Color de Filas Impares" 
                value={tableRowOddColor} 
                onChange={setTableRowOddColor} 
                backgroundColor={tableRowOddColor}
                previewText="Fila Impar"
                textColor={globalFontColor}
                height={100}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <ColorField 
                title="Color de Filas Pares" 
                value={tableRowEvenColor} 
                onChange={setTableRowEvenColor} 
                backgroundColor={tableRowEvenColor}
                previewText="Fila Par"
                textColor={globalFontColor}
                height={100}
              />
            </Grid>
          </>
        )}
        
        {/* SECCIÓN 2: CRONÓMETROS - No mostrar cronómetro grande para campo en vista individual */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
            <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Cronómetros
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        
        {/* Cronómetro pequeño - Siempre visible */}
        <Grid item xs={12} sm={6} md={4}>
          <ColorField 
            title="Cronómetro Pequeño" 
            value={timerColor} 
            onChange={setTimerColor} 
            backgroundColor={tableBackgroundColor}
            previewText="00:12.34"
            textColor={timerColor}
            fontSize={24}
            fontFamily="monospace"
          />
        </Grid>
        
        {/* Cronómetro grande - Solo para pista o campo en vista múltiple */}
        {(isTrackBoard || (isFieldBoard && isMultiAthleteView)) && (
          <Grid item xs={12} sm={6} md={4}>
            <ColorField 
              title="Cronómetro Grande" 
              value={largeTimerColor} 
              onChange={setLargeTimerColor} 
              backgroundColor={tableBackgroundColor}
              previewText="01:23.45"
              textColor={largeTimerColor}
              fontSize={36}
              fontFamily="monospace"
            />
          </Grid>
        )}
        
        {/* SECCIÓN 3: ENCABEZADOS */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
            <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Encabezados
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        
        {/* Fondo y texto del encabezado - Siempre visibles */}
        <Grid item xs={12} sm={6} md={4}>
          <ColorField 
            title="Fondo del Encabezado" 
            value={headerBackgroundColor} 
            onChange={setHeaderBackgroundColor} 
            backgroundColor={headerBackgroundColor}
            previewText="Final 100m"
            textColor={headerFontColor}
            fontSize={20}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <ColorField 
            title="Texto del Encabezado" 
            value={headerFontColor} 
            onChange={setHeaderFontColor} 
            backgroundColor={headerBackgroundColor}
            previewText="Final 100m"
            textColor={headerFontColor}
            fontSize={20}
          />
        </Grid>
        
        {/* Encabezados de columnas - Solo en vista múltiple */}
        {isMultiAthleteView && (
          <Grid item xs={12} sm={6} md={4}>
            <ColorField 
              title="Encabezados de Columnas" 
              value={columnsHeaderColor} 
              onChange={setColumnsHeaderColor} 
              backgroundColor={columnsHeaderColor}
              previewText="Nombre Tiempo Pos"
              textColor={headerFontColor}
              fontSize={16}
            />
          </Grid>
        )}

        {/* SECCIÓN 4: VISTA ESPECÍFICA PARA CAMPO (1 ATLETA) */}
        {isFieldBoard && isSingleAthleteView && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
                <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                Colores para Vista Individual de Campo
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            {/* Posición (fondo y texto) */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Fondo de Posición" 
                value={positionBackgroundColor} 
                onChange={setPositionBackgroundColor} 
                backgroundColor={positionBackgroundColor}
                previewText="1"
                textColor={positionColor}
                fontSize={24}
                fontWeight="bold"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Texto de Posición" 
                value={positionColor} 
                onChange={setPositionColor} 
                backgroundColor={positionBackgroundColor}
                previewText="1"
                textColor={positionColor}
                fontSize={24}
                fontWeight="bold"
              />
            </Grid>
            
            {/* Nombre/Equipo (fondo y texto) */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Fondo de Nombre/Equipo" 
                value={nameBackgroundColor} 
                onChange={setNameBackgroundColor} 
                backgroundColor={nameBackgroundColor}
                previewText="Atleta"
                textColor={athleteNameColor}
                fontSize={20}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Texto de Nombre" 
                value={athleteNameColor} 
                onChange={setAthleteNameColor} 
                backgroundColor={nameBackgroundColor}
                previewText="Juan Pérez"
                textColor={athleteNameColor}
                fontSize={20}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Texto de Equipo" 
                value={teamNameColor} 
                onChange={setTeamNameColor} 
                backgroundColor={nameBackgroundColor}
                previewText="Equipo"
                textColor={teamNameColor}
                fontSize={16}
              />
            </Grid>
            
            {/* Marca (fondo y texto) */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Fondo de Marca" 
                value={markBackgroundColor} 
                onChange={setMarkBackgroundColor} 
                backgroundColor={markBackgroundColor}
                previewText="8.25 m"
                textColor={resultTimeColor}
                fontSize={20}
                fontFamily="monospace"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Texto de Marca" 
                value={resultTimeColor} 
                onChange={setResultTimeColor} 
                backgroundColor={markBackgroundColor}
                previewText="8.25 m"
                textColor={resultTimeColor}
                fontSize={20}
                fontFamily="monospace"
              />
            </Grid>
            
            {/* Intento (solo para campo horizontal) */}
            {isHorizontalField && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Fondo de Intento" 
                    value={attemptBackgroundColor} 
                    onChange={setAttemptBackgroundColor} 
                    backgroundColor={attemptBackgroundColor}
                    previewText="Intento #3"
                    textColor={attemptColor}
                    fontSize={16}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Texto de Intento" 
                    value={attemptColor} 
                    onChange={setAttemptColor} 
                    backgroundColor={attemptBackgroundColor}
                    previewText="Intento #3"
                    textColor={attemptColor}
                    fontSize={16}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Texto de Viento" 
                    value={deltaTimeColor} 
                    onChange={setDeltaTimeColor} 
                    backgroundColor={markBackgroundColor}
                    previewText="+1.2 m/s"
                    textColor={deltaTimeColor}
                    fontSize={14}
                  />
                </Grid>
              </>
            )}
            
            {/* Colores de intentos (solo para campo vertical) */}
            {isVerticalField && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Intento Exitoso" 
                    value={successColor} 
                    onChange={setSuccessColor} 
                    backgroundColor={successColor}
                    previewText="O"
                    textColor="#ffffff"
                    fontSize={20}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Intento Fallido" 
                    value={failColor} 
                    onChange={setFailColor} 
                    backgroundColor={failColor}
                    previewText="X"
                    textColor="#ffffff"
                    fontSize={20}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Intento Pasado" 
                    value={passColor} 
                    onChange={setPassColor} 
                    backgroundColor={passColor}
                    previewText="P"
                    textColor="#ffffff"
                    fontSize={20}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* SECCIÓN 5: ELEMENTOS ESPECÍFICOS PARA VISTA MÚLTIPLE (APLICABLE A TODOS LOS TIPOS) */}
        {isMultiAthleteView && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
                <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                Colores Personalizados - Información Principal
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            {/* Posición siempre se muestra si hay resultados */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Posición" 
                value={positionColor} 
                onChange={setPositionColor} 
                backgroundColor={tableBackgroundColor}
                previewText="1"
                textColor={positionColor}
                fontSize={22}
                fontWeight="bold"
              />
            </Grid>
            
            {/* Carril/Línea solo si está habilitado */}
            {showLane && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Carril/Línea" 
                  value={laneColor} 
                  onChange={setLaneColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="4"
                  textColor={laneColor}
                  fontSize={20}
                />
              </Grid>
            )}
            
            {/* Número/Dorsal solo si está habilitado */}
            {showNumber && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Número/Dorsal" 
                  value={numberColor} 
                  onChange={setNumberColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="123"
                  textColor={numberColor}
                  fontSize={20}
                />
              </Grid>
            )}
            
            {/* Nombre de Atleta siempre se muestra */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Nombre de Atleta" 
                value={athleteNameColor} 
                onChange={setAthleteNameColor} 
                backgroundColor={tableBackgroundColor}
                previewText="Juan Pérez"
                textColor={athleteNameColor}
                fontSize={18}
                fontWeight="bold"
              />
            </Grid>
            
            {/* Nombre de Equipo siempre se muestra */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Nombre de Equipo" 
                value={teamNameColor} 
                onChange={setTeamNameColor} 
                backgroundColor={tableBackgroundColor}
                previewText="Equipo Rojo"
                textColor={teamNameColor}
                fontSize={16}
              />
            </Grid>
            
            {/* Tiempo/Marca siempre se muestra */}
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title={isFieldBoard ? "Marca" : "Tiempo"} 
                value={resultTimeColor} 
                onChange={setResultTimeColor} 
                backgroundColor={tableBackgroundColor}
                previewText={isFieldBoard ? "8.25 m" : "10.45"}
                textColor={resultTimeColor}
                fontSize={18}
                fontFamily="monospace"
              />
            </Grid>
          </>
        )}
        
        {/* SECCIÓN 6: ELEMENTOS ESPECÍFICOS DE PISTA */}
        {isTrackBoard && isMultiAthleteView && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
                <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                Colores Personalizados - Elementos Adicionales de Pista
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            {/* Diferencia de Tiempo solo si está habilitado */}
            {showTimeDifference && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Diferencia de Tiempo" 
                  value={deltaTimeColor} 
                  onChange={setDeltaTimeColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="+0.25"
                  textColor={deltaTimeColor}
                  fontSize={16}
                />
              </Grid>
            )}
            
            {/* Tiempo Última Vuelta solo si está habilitado */}
            {showLastLapTime && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Tiempo Última Vuelta" 
                  value={lastLapTimeColor} 
                  onChange={setLastLapTimeColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="01:15.32"
                  textColor={lastLapTimeColor}
                  fontSize={16}
                  fontFamily="monospace"
                />
              </Grid>
            )}
            
            {/* Tiempo de Reacción solo si está habilitado */}
            {showReactionTime && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Tiempo de Reacción" 
                  value={reactionTimeColor} 
                  onChange={setReactionTimeColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="0.156"
                  textColor={reactionTimeColor}
                  fontSize={16}
                  fontFamily="monospace"
                />
              </Grid>
            )}
            
            {/* Vueltas Faltantes solo si está habilitado */}
            {showLapsToGo && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Vueltas Faltantes" 
                  value={lapsToGoColor} 
                  onChange={setLapsToGoColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="2"
                  textColor={lapsToGoColor}
                  fontSize={18}
                />
              </Grid>
            )}
            
            {/* Columnas de Vueltas solo si está habilitado */}
            {showLapsColumns && (
              <Grid item xs={12} sm={6} md={4}>
                <ColorField 
                  title="Columnas de Vueltas" 
                  value={lapsColumnsColor} 
                  onChange={setLapsColumnsColor} 
                  backgroundColor={tableBackgroundColor}
                  previewText="V1: 01:24.56"
                  textColor={lapsColumnsColor}
                  fontSize={16}
                  fontFamily="monospace"
                />
              </Grid>
            )}
          </>
        )}

        {/* SECCIÓN 7: ELEMENTOS ESPECÍFICOS DE CAMPO HORIZONTAL (MULTIPLE) */}
        {isHorizontalField && isMultiAthleteView && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
                <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                Colores Personalizados - Elementos de Campo Horizontal
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Viento" 
                value={deltaTimeColor} 
                onChange={setDeltaTimeColor} 
                backgroundColor={tableBackgroundColor}
                previewText="+1.2 m/s"
                textColor={deltaTimeColor}
                fontSize={14}
              />
            </Grid>
            
            {/* Número de Intento solo si está habilitado */}
            {showAttemptNumber && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Número de Intento" 
                    value={attemptColor} 
                    onChange={setAttemptColor} 
                    backgroundColor={attemptBackgroundColor}
                    previewText="Intento #3"
                    textColor={attemptColor}
                    fontSize={14}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <ColorField 
                    title="Fondo de Intento" 
                    value={attemptBackgroundColor} 
                    onChange={setAttemptBackgroundColor} 
                    backgroundColor={attemptBackgroundColor}
                    previewText="Intento #3"
                    textColor={attemptColor}
                    fontSize={14}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        
        {/* SECCIÓN 8: ELEMENTOS ESPECÍFICOS DE CAMPO VERTICAL (MULTIPLE) */}
        {isVerticalField && isMultiAthleteView && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ mt: 3 }}>
                <ColorLens sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                Colores Personalizados - Elementos de Campo Vertical
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Intento Exitoso" 
                value={successColor} 
                onChange={setSuccessColor} 
                backgroundColor={successColor}
                previewText="O"
                textColor="#ffffff"
                fontSize={16}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Intento Fallido" 
                value={failColor} 
                onChange={setFailColor} 
                backgroundColor={failColor}
                previewText="X"
                textColor="#ffffff"
                fontSize={16}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <ColorField 
                title="Intento Pasado" 
                value={passColor} 
                onChange={setPassColor} 
                backgroundColor={passColor}
                previewText="P"
                textColor="#ffffff"
                fontSize={16}
              />
            </Grid>
          </>
        )}
      </Grid>
      
      {/* Mensaje si no hay elementos configurables */}
      {isMultiAthleteView && Object.entries({
        lane: showLane,
        number: showNumber,
        attemptNumber: isHorizontalField && showAttemptNumber,
        timeDifference: isTrackBoard && showTimeDifference,
        lastLapTime: isTrackBoard && showLastLapTime,
        reactionTime: isTrackBoard && showReactionTime,
        lapsToGo: isTrackBoard && showLapsToGo,
        lapsColumns: isTrackBoard && showLapsColumns
      }).every(([_, value]) => !value) && (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            mt: 3,
            backgroundColor: 'info.light',
            color: 'info.contrastText'
          }}
        >
          <Typography variant="body2">
            Para ver más opciones de personalización de colores, active las columnas adicionales
            en la pestaña de "Columnas".
          </Typography>
        </Paper>
      )}
    </>
  );
};

export default ColoresTab;